import {
  type SxProps,
  Tab as MuiTab,
  type TabProps as MuiTabProps,
  Tabs as MuiTabs,
  type TabsProps as MuiTabsProps,
  type Theme,
} from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { Children, cloneElement, isValidElement, type ReactElement, type ReactNode } from "react";

export const TAB_SIZES = ["normal", "large"] as const;
export type TabSize = (typeof TAB_SIZES)[number];

export const TAB_VARIANTS = ["default", "pill", "invertedPill"] as const;
export type TabVariant = (typeof TAB_VARIANTS)[number];

type BaseTabProps = Omit<MuiTabProps, "variant">;

export interface TabProps extends BaseTabProps {
  size?: TabSize;
  tabVariant?: TabVariant;
}

export function Tab(props: TabProps) {
  const { size = "normal", tabVariant = "default", sx, ...restProps } = props;

  return <MuiTab {...restProps} size={size} sx={mergeSxProps(getTabVariantSx(tabVariant), sx)} />;
}

export interface TabsProps extends Omit<MuiTabsProps, "variant"> {
  size?: TabSize;
  variant?: TabVariant;
  equalWidth?: boolean;
  children?: ReactNode;
}

function isValidTabElement(child: unknown): child is ReactElement<{ tabVariant?: TabVariant }> {
  return isValidElement(child);
}

function injectTabVariant(child: unknown, variant: TabVariant): ReactNode {
  if (!isValidTabElement(child)) {
    return child as ReactNode;
  }

  return cloneElement(child, {
    tabVariant: child.props.tabVariant ?? variant,
  });
}

export function Tabs(props: TabsProps) {
  const {
    size = "normal",
    variant = "default",
    equalWidth = false,
    children,
    sx,
    ...restProps
  } = props;

  const childrenWithVariant = Children.map(children, (child) => injectTabVariant(child, variant));

  return (
    <MuiTabs
      {...restProps}
      size={size}
      sx={mergeSxProps(
        getTabsVariantSx(variant),
        mergeSxProps(
          equalWidth
            ? { width: "100%", "& .MuiTab-root": { flex: 1, maxWidth: "none" } }
            : undefined,
          sx
        )
      )}
      variant="scrollable"
      scrollButtons={false}
    >
      {childrenWithVariant}
    </MuiTabs>
  );
}

function getTabsVariantSx(variant: TabVariant): SxProps<Theme> | undefined {
  switch (variant) {
    case "pill":
      return (theme) => ({
        padding: theme.spacing(1),
        "& .MuiTabs-flexContainer": {
          gap: theme.spacing(3),
        },
        "& .MuiTabs-scroller": {
          overflow: "visible !important",
        },
        "& .MuiTabs-indicator": {
          display: "none",
        },
      });
    case "invertedPill":
      return (theme) => ({
        backgroundColor: theme.palette.background?.muted,
        borderRadius: theme.borderRadius?.large,
        minHeight: "auto",
        "& .MuiTabs-flexContainer": {
          gap: 0,
          height: "auto",
        },
        "& .MuiTabs-indicator": {
          display: "none",
        },
      });
    case "default":
      return undefined;
  }
}

function getTabVariantSx(variant: TabVariant): SxProps<Theme> | undefined {
  switch (variant) {
    case "pill":
      return (theme) => ({
        padding: `${theme.spacing(2)} ${theme.spacing(5)}`,
        borderRadius: theme.borderRadius?.xxLarge,
        position: "relative" as const,
        zIndex: 3,
        backgroundColor: "transparent",
        transition: "background-color 0.3s ease",

        "&.Mui-selected": {
          backgroundColor: theme.palette.background.inactive,
          color: theme.palette.text.primary,
        },
      });
    case "invertedPill":
      return (theme) => ({
        padding: `${theme.spacing(3)} ${theme.spacing(5)}`,
        borderRadius: theme.borderRadius?.large,
        border: `3px solid transparent`,
        zIndex: 3,
        color: theme.palette.base?.inactive,
        fontSize: theme.typography.body2.fontSize,

        "&.Mui-selected": {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `3px solid ${theme.palette.background?.muted}`,
        },
        "&.Mui-disabled": {
          borderColor: "transparent",
        },
      });
    case "default":
      return undefined;
  }
}
